import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  MessageSquareIcon,
  RefreshCwIcon,
  RocketIcon,
  SearchIcon,
  SparklesIcon,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { type Project } from '@/lib/queries/project';
import { incidentsManagementPagerDutyIncidentsQueries } from '@/lib/queries/rca/incidents';
import { refreshPastIncidents } from '@/lib/queries/slack';
import { generateMutationKey } from '@/lib/queries/utils';

import { Integrations } from './Integrations';

interface EmptyIncidentsProps {
  defaultProjectId: string;
  project: Project;
}

export const EmptyIncidents = ({
  defaultProjectId,
  project,
}: EmptyIncidentsProps): JSX.Element => {
  const { toast } = useToast();
  const { mutateAsync, isPending } = useMutation({
    mutationKey: generateMutationKey(
      incidentsManagementPagerDutyIncidentsQueries.list(defaultProjectId),
      'refresh-slack',
      true,
    ),
    mutationFn: async () => {
      toast({
        title: 'rca.emptyStates.toast.scanningTitle',
        description: 'rca.emptyStates.toast.scanningDescription',
      });
      await refreshPastIncidents();
    },
    onSuccess: () => {
      toast({
        title: 'rca.emptyStates.toast.importCompleteTitle',
        description: 'rca.emptyStates.toast.importCompleteDescription',
      });
    },
    onError: error => {
      console.error('Failed to fetch past incidents:', error);
      toast({
        title: 'rca.emptyStates.toast.importFailedTitle',
        description: 'rca.emptyStates.toast.importFailedDescription',
        variant: 'destructive',
      });
    },
  });

  // Check if Slack is installed
  const isSlackInstalled = project.installedOnChannel === true;

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center max-w-4xl mx-auto">
      {/* Icon and Header */}
      <div className="flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
        <SparklesIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
      </div>

      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        <FormattedMessage id="rca.emptyStates.title" />
      </h2>

      {/* Conditional Content Based on Slack Installation */}
      {isSlackInstalled ? (
        // Slack is installed - show original content
        <div className="grid md:grid-cols-2 gap-6 w-full mb-4 max-w-2xl">
          {/* Tag Annie Card */}
          <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
            <div className="flex items-center mb-3">
              <MessageSquareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                <FormattedMessage id="rca.emptyStates.tagAnnie.title" />
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              <FormattedMessage id="rca.emptyStates.tagAnnie.description" />
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-white dark:bg-gray-900 p-2 rounded border">
              <FormattedMessage id="rca.emptyStates.tagAnnie.example" />
            </div>
          </div>

          {/* Fetch Past Incidents Card */}
          <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50">
            <div className="flex items-center mb-3">
              <SearchIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                <FormattedMessage id="rca.emptyStates.importPastIncidents.title" />
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              <FormattedMessage id="rca.emptyStates.importPastIncidents.description" />
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => mutateAsync()}
              disabled={isPending}
            >
              {isPending ? (
                <RefreshCwIcon className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <SearchIcon className="w-4 h-4 mr-2" />
              )}
              {isPending ? (
                <FormattedMessage id="rca.emptyStates.importPastIncidents.buttonScanning" />
              ) : (
                <FormattedMessage id="rca.emptyStates.importPastIncidents.buttonFetch" />
              )}
            </Button>
          </div>
        </div>
      ) : (
        // Slack is not installed - show getting started invitation
        <div className="w-full mb-4 max-w-2xl">
          <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
            <div className="flex items-center mb-3">
              <RocketIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                <FormattedMessage id="rca.emptyStates.slackNotInstalled.title" />
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 text-left">
              <FormattedMessage id="rca.emptyStates.slackNotInstalled.description" />
            </p>
            <Link to="/getting-started">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <RocketIcon className="w-4 h-4 mr-2" />
                <FormattedMessage id="rca.emptyStates.slackNotInstalled.buttonText" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      <Integrations defaultProjectId={defaultProjectId} />
    </div>
  );
};

interface NoFilteredIncidentsProps {
  onClearFilters: () => void;
}

export const NoFilteredIncidents = ({
  onClearFilters,
}: NoFilteredIncidentsProps): JSX.Element => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="text-gray-500 dark:text-gray-400 mb-4">
      <FormattedMessage id="incidents.noFilteredIncidents" />
    </div>
    <Button onClick={onClearFilters} variant="outline">
      <FormattedMessage id="incidents.filters.clear" />
    </Button>
  </div>
);
